import { useEffect, useState } from "react";
import { useTeamsQuery } from "../../../../Services/settingsAPIs/CommonAPI";
import {
  useTicketRulesByTeamQuery,
  useUdpateTicketRulesByTeamMutation,
} from "../../../../Services/settingsAPIs/TicketSettingsAPI";
import { ReactMultiEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../LoadingSpinner";
import { capitalizeFirstLetter } from "../../../reusableComponent/CapitalLetter";

const MIN_SLA_MS = 60 * 60 * 1000;

const SLA_DURATION_ZERO_MSG =
  "Hours and minutes cannot both be zero. Set at least one.";

const SLA_DURATION_MIN_MSG =
  "SLA duration must be at least 1 hour when set. Increase hours or minutes.";

const hasAtLeastOneEmail = (emails: unknown): boolean =>
  Array.isArray(emails) &&
  emails.some((e) => String(e ?? "").trim().length > 0);

/** Allow both unset; if either is set, both must be set (same idea as tag-based SLA). */
const validatePrioritySlaTimerAndEmail = (
  durationInMs: number,
  rowEmails: unknown
): { ok: true } | { ok: false; message: string } => {
  const emailsOk = hasAtLeastOneEmail(rowEmails);
  const hasDuration = durationInMs > 0;
  const durationMeetsMin = durationInMs >= MIN_SLA_MS;

  if (hasDuration && durationMeetsMin && emailsOk) return { ok: true };
  if (!hasDuration && !emailsOk) return { ok: true };
  if (hasDuration && !durationMeetsMin) {
    return { ok: false, message: SLA_DURATION_MIN_MSG };
  }
  if (hasDuration && !emailsOk) {
    return {
      ok: false,
      message:
        "SL duration is set but no notification email. Add at least one email.",
    };
  }
  return {
    ok: false,
    message:
      "Notification email is set but SL timer is not. Set hours or minutes.",
  };
};

const PriorityBasedSLA = ({ setSelectedPriority }) => {
  const [selectedTeam, setSelectedTeam] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSlaLoading, setIsSlaLoading] = useState(false);
  const { data: teamsData } = useTeamsQuery({});
  const [theTeams, setTheTeams] = useState([]);
  const [emails, setEmails] = useState([]);
  const [slaRules, setSlaRules] = useState([]);
  const [originalEmails, setOriginalEmails] = useState([]);
  const [originalSlaRules, setOriginalSlaRules] = useState([]);
  const {
    data: ticketData,
    refetch,
    isFetching,
  } = useTicketRulesByTeamQuery(selectedTeam, {
    skip: !selectedTeam,
  });

  const [triggerUpdateOnTicketRulesByTeamId] =
    useUdpateTicketRulesByTeamMutation();

  useEffect(() => {
    if (isFetching) {
      setIsSlaLoading(true);
    } else {
      setIsSlaLoading(false);
    }
  }, [isFetching]);

  useEffect(() => {
    if (ticketData) {
      setEmails(ticketData.ticket_resolution_sla_emails || []);
      setOriginalEmails(ticketData.ticket_resolution_sla_emails || []);
      const formattedSlaRules = Object.entries(ticketData.sla_rules || {}).map(
        ([priority, time]) => ({
          priority,
          time,
        })
      );
      setSlaRules(formattedSlaRules);
      setOriginalSlaRules(formattedSlaRules);
    }
  }, [ticketData]);

  useEffect(() => {
    setTheTeams(teamsData || []);
  }, [teamsData]);

  useEffect(() => {
    setSelectedPriority(!!selectedTeam);
  }, [selectedTeam]);

  const handleTimeChange = (index, type, action) => {
    setSlaRules((prevRules) => {
      return prevRules.map((rule, i) => {
        if (i === index) {
          let newTime = rule.time;
          if (type === "hours") {
            if (action === "increment") {
              newTime += 60 * 60 * 1000;
            } else if (action === "decrement" && newTime >= 60 * 60 * 1000) {
              newTime -= 60 * 60 * 1000;
            }
          } else if (type === "minutes") {
            if (action === "increment") {
              newTime += 60 * 1000;
            } else if (action === "decrement" && newTime >= 60 * 1000) {
              newTime -= 60 * 1000;
            }
          }

          if (
            action === "increment" &&
            newTime > 0 &&
            newTime < MIN_SLA_MS
          ) {
            newTime = MIN_SLA_MS;
          }

          if (
            action === "decrement" &&
            newTime === 0 &&
            hasAtLeastOneEmail(emails[rule.priority])
          ) {
            toast.error(SLA_DURATION_ZERO_MSG);
            return rule;
          }

          if (action === "decrement" && newTime > 0 && newTime < MIN_SLA_MS) {
            toast.error(SLA_DURATION_MIN_MSG);
            return rule;
          }

          return { ...rule, time: newTime };
        }
        return rule;
      });
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!selectedTeam) {
      toast.error("Please select a team first.");
      return;
    }

    setIsLoading(true);

    for (const { priority, time } of slaRules) {
      const rowEmails = emails[priority];
      const rowCheck = validatePrioritySlaTimerAndEmail(time, rowEmails);
      if (rowCheck.ok === false) {
        toast.error(rowCheck.message);
        setIsLoading(false);
        return;
      }
    }

    const payload = {
      ticket_resolution_sla_emails: emails,
      sla_rules: slaRules.reduce((acc, { priority, time }) => {
        acc[priority] = time;
        return acc;
      }, {}),
    };

    try {
      const response = await triggerUpdateOnTicketRulesByTeamId({
        body: payload,
        teamId: selectedTeam,
      }).unwrap();

      if (response) {
        toast.success("SL rules updated successfully!");
        await refetch();
      }
    } catch (error) {
      toast.error("Failed to update SL rules.");
    } finally {
      setIsLoading(false);
    }
  };

  const isChanged =
    JSON.stringify(emails) !== JSON.stringify(originalEmails) ||
    JSON.stringify(slaRules) !== JSON.stringify(originalSlaRules);

  return (
    <div>
      <div
        className="card-toolbar flex-row-fluid justify-content-end gap-5 p-5"
        style={{ marginLeft: "0%" }}
      >
        <div className="row mb-2">
          <div className="w-100 mw-200px">
            <label>Select team</label>
            <select
              className="form-select"
              onChange={(e) => setSelectedTeam(e.target.value)}
              value={selectedTeam}
              disabled={isLoading}
            >
              <option value="">Select team</option>
              {theTeams.map((team, index) => (
                <option key={index} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedTeam && (
          <>
            <div className="row">
              <div className="col">
                <div
                  className="card my-1 min-h-40px justify-content-center"
                  style={{ backgroundColor: "rgba(228, 228, 228, 0.498)" }}
                >
                  <div className="row align-items-center">
                    <div className="col-4 text-start ps-10">
                      <strong>Priority</strong>
                    </div>
                    <div className="col-1 text-center">
                      <strong>Hours</strong>
                    </div>
                    <div className="col-1"></div>
                    <div className="col-1 text-center">
                      <strong>Minutes</strong>
                    </div>
                    <div className="col-1"></div>
                    <div className="col-4 text-center">
                      <strong>Mail ID</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {isSlaLoading ? (
              <div className="text-center mt-3">
                <LoadingSpinner />
              </div>
            ) : (
              slaRules?.map(({ priority, time }, index) => (
                <div className="row" key={index}>
                  {/* <div className="col"> */}
                  <div className="input-group-text mt-2 bg-white">
                    <div className="row w-100 align-items-center">
                      <div className="col-4 text-start ps-5">
                        {capitalizeFirstLetter(priority)}
                      </div>
                      <div className="col-1 d-flex align-items-center justify-content-center">
                        {/* <input
                            type="number"
                            className="form-control border-0 py-0 text-center"
                            value={Math.floor(time / (1000 * 60 * 60))}
                            min={0}
                            readOnly
                          /> */}
                        {Math.floor(time / (1000 * 60 * 60))}
                        <div className="d-flex flex-column ms-2">
                          <i
                            className="bi bi-caret-up-fill cursor-pointer"
                            onClick={() =>
                              handleTimeChange(index, "hours", "increment")
                            }
                          ></i>
                          <i
                            className="bi bi-caret-down-fill cursor-pointer"
                            onClick={() =>
                              handleTimeChange(index, "hours", "decrement")
                            }
                          ></i>
                        </div>
                      </div>
                      <div className="col-1"></div>
                      <div className="col-1 d-flex align-items-center justify-content-center">
                        {/* <input
                            type="number"
                            className="form-control border-0 py-0 text-center"
                            value={Math.floor((time / (1000 * 60)) % 60)}
                            min={0}
                            readOnly
                          /> */}

                        {Math.floor((time / (1000 * 60)) % 60)}
                        <div className="d-flex flex-column ms-2">
                          <i
                            className="bi bi-caret-up-fill cursor-pointer"
                            onClick={() =>
                              handleTimeChange(index, "minutes", "increment")
                            }
                          ></i>
                          <i
                            className="bi bi-caret-down-fill cursor-pointer"
                            onClick={() =>
                              handleTimeChange(index, "minutes", "decrement")
                            }
                          ></i>
                        </div>
                      </div>
                      <div className="col-1"></div>
                      <div className="col-4 text-center">
                        <ReactMultiEmail
                          placeholder="Enter Email"
                          emails={emails[priority] || []} // Retrieve emails for specific priority
                          onChange={(updatedEmails) => {
                            setEmails((prev) => ({
                              ...prev,
                              [priority]: updatedEmails, // Update only the specific priority's emails
                            }));
                          }}
                          getLabel={(email, index, removeEmail) => (
                            <div data-tag key={index}>
                              {email}
                              <span
                                data-tag-handle
                                onClick={() => removeEmail(index)}
                              >
                                ×
                              </span>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {/* </div> */}
                </div>
              ))
            )}
            <div className="row">
              <div className="col text-end mt-2">
                <button
                  type="button"
                  className="btn btn-primary ms-4 min-w-100px"
                  onClick={handleSave}
                  disabled={isLoading || !isChanged}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PriorityBasedSLA;